// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Circle from "src/assets/shapes/Circle/Circle";
import Square from "src/assets/shapes/Square/Square";

// Utils
import { getDecimalNum } from "src/utils/number/numberHelper";

// Types
import { ChartSpotType } from "src/services/exams/types";

const TOTAL_SPOTS_BINOCULAR = 120;
const TOTAL_SPOTS_MONOCULAR = 80;

type EstermanChartLegendType = {
  estermanSpots: Array<ChartSpotType>;
  isBinocular: boolean;
};

const EstermanChartLegend = ({
  estermanSpots,
  isBinocular,
}: EstermanChartLegendType) => {
  const { t } = useTranslation();

  const seenSpots = useMemo(
    () => estermanSpots?.filter((point) => point?.seen),
    [estermanSpots]
  );

  const totalSpots = useMemo(
    () => (isBinocular ? TOTAL_SPOTS_BINOCULAR : TOTAL_SPOTS_MONOCULAR),
    [isBinocular]
  );

  const efficiencyScore = useMemo(() => {
    const seenPercentage = (seenSpots?.length / totalSpots) * 100;

    // return an integer
    if (Number.isInteger(seenPercentage)) return seenPercentage;

    // return a floating point with 2 decimal places
    return getDecimalNum(seenPercentage, 2);
  }, [seenSpots?.length, totalSpots]);

  return (
    <div>
      <Box display="flex" alignItems="center" gap="8px">
        <Circle />
        <Typography fontSize="14px">
          <span style={{ fontWeight: 600 }}>{t("Seen")}:</span>{" "}
          {seenSpots?.length}/{totalSpots}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap="8px">
        <Square />
        <Typography fontSize="14px">
          <span style={{ fontWeight: 600 }}>{t("Not Seen")}:</span>{" "}
          {totalSpots - seenSpots?.length}/{totalSpots}
        </Typography>
      </Box>
      <Box display="flex">
        <Typography fontSize="14px">
          <span style={{ fontWeight: 600 }}>
            {t("Esterman Efficiency Score")}:
          </span>{" "}
          {efficiencyScore}
        </Typography>
      </Box>
    </div>
  );
};

export default EstermanChartLegend;
